import { Pause, RocketLaunch } from '@phosphor-icons/react';
import { Button, Flex, SettingsSection, TagBadge, Text } from '@chatsdet/ui';
import type { WorkflowStatus } from './constants';

interface SuiteInclusionSettingsProps {
  currentPromotion: 'promoted' | 'paused' | null;
  currentStatus: WorkflowStatus;
  isPending: boolean;
  isPromoting: boolean;
  isPausing: boolean;
  onPromote: () => void | Promise<void>;
  onPause: () => void | Promise<void>;
}

export function SuiteInclusionSettings({
  currentPromotion,
  currentStatus,
  isPending,
  isPromoting,
  isPausing,
  onPromote,
  onPause,
}: SuiteInclusionSettingsProps) {
  const isPromoted = currentPromotion === 'promoted';

  return (
    <SettingsSection
      title="Suite inclusion"
      description="Whether this flow runs when test suites run."
    >
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-card p-3">
        <div className="flex flex-col gap-1">
          <Flex align="center" gap="sm">
            <Text size="sm" weight="medium">
              Promotion
            </Text>
            <TagBadge
              name={
                isPromoted ? 'Promoted' : currentPromotion === 'paused' ? 'Paused' : 'Not promoted'
              }
              color={isPromoted ? 'blue' : 'gray'}
              hideDot
            />
          </Flex>
          <Text size="xs" className="text-muted-foreground">
            {isPromoted
              ? 'This flow runs as part of test suites.'
              : 'This flow is excluded from suite runs.'}
          </Text>
        </div>
        {isPromoted ? (
          <Button
            variant="outline"
            size="sm"
            onPress={() => void onPause()}
            isDisabled={isPending}
          >
            <Pause size={16} />
            {isPausing ? 'Pausing…' : 'Pause'}
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onPress={() => void onPromote()}
            isDisabled={isPending || currentStatus !== 'approved'}
          >
            <RocketLaunch size={16} />
            {isPromoting ? 'Promoting…' : 'Promote'}
          </Button>
        )}
      </div>
      {!isPromoted && currentStatus !== 'approved' && (
        <Text size="xs" className="text-muted-foreground">
          Approve this flow before it can be promoted.
        </Text>
      )}
    </SettingsSection>
  );
}
